import { Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material";
import React from "react";
import ReactMarkdown from "react-markdown";
import { useHistory, useLocation } from "react-router-dom";
import { CourseApi } from "../api/CourseApi";
import { DialogApi, IDialog } from "../api/DialogApi";
import { ILesson } from "../api/LessonApi";
import AppBarContext from '../context/AppBarContext';
import { handleErrorMessage } from './ErrorDialog';
// npm install react-markdown@7.1.2 --legacy-peer-deps

interface LessonViewProps {
    lesson: ILesson;
}

const LessonView: React.FC<LessonViewProps> = ({ lesson }) => {
    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const [, setCourseName] = React.useState("");
    const [, setLessonName] = React.useState(lesson.name);
    const [lessonDescription, setLessonDescription] = React.useState(lesson.description);
    const [dialogList, setDialogList] = React.useState<IDialog[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);

    React.useEffect(() => {
        setLessonName(lesson.name);
        setLessonDescription(lesson.description || '');
        setIsLoading(true);
        setShowSkeleton(false); // reset

        const skeletonDelay = setTimeout(() => {
            setShowSkeleton(true);
        }, 300); // delay

        // Fetch course name
        const coursePromise = CourseApi.getById(lesson.course_id)
            .then((response) => {
                if (response.status < 300) return response.data;
                throw new Error("Unable to CourseApi.getById() " + response.status);
            })
            .then((course) => setCourseName(course.name))
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });

        // Fetch dialogs for this lesson
        const dialogPromise = DialogApi.getAll(lesson.id)
            .then((response) => {
                if (response.status < 300) return response.data;
                throw new Error("Unable to DialogApi.getAll() " + response.status);
            })
            .then((dialogList) => {
                const visibleList = dialogList.filter((dialog) => dialog.is_hidden === false);
                setDialogList(visibleList.sort((a, b) => a.order_index - b.order_index));
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });

        Promise.all([coursePromise, dialogPromise])
            .finally(() => {
                setIsLoading(false);
            });

        return () => {
            clearTimeout(skeletonDelay); // clean up
        };

    }, [lesson, history, location, setErrorMessage]);

    const handleDialogClick = (dialog: IDialog) => {
        // Navigate to /lesson/:courseId/:lessonId/:dialogId
        history.push(`/lesson/${lesson.course_id}/${lesson.id}/${dialog.id}`);
    };

    if (isLoading && showSkeleton) {
        return (
            <Stack spacing={2} sx={{ width: '100%', pt: 2, px: 4 }}>
                {/* Description skeleton */}
                <Skeleton variant="text" animation="wave" width="60%" height={32} />
                <Skeleton variant="text" animation="wave" width="40%" height={24} />

                {/* Grid of dialog skeletons */}
                <Grid container spacing={2}>
                    {[...Array(6)].map((_, index) => (
                        <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                            <Skeleton
                                variant="rectangular"
                                height={80}
                                animation="wave"
                                sx={{ borderRadius: 2 }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        );
    }

    return (
        <Stack
            spacing={4}
            sx={{
                width: "100%",
                pt: 2,
                pl: 4,
                whiteSpace: "pre-line",
                alignItems: "flex-start", // 👈 aligns children (like Typography) to the left
            }}
        >
            <Typography sx={{ textAlign: "left", width: "100%", ml: 4 }}>
                <ReactMarkdown>{lessonDescription || ""}</ReactMarkdown>
            </Typography>

            <Stack sx={{ width: "100%", mt: 4 }}>
            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
                gap={{ xs: 2, sm: 3, md: 4 }}
                justifyContent="flex-start"
                sx={{
                    py: 2, // even top & bottom padding
                    px: 1, // slight horizontal padding
                }}
            >
                {(isLoading && showSkeleton)
                ? [...Array(6)].map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rectangular"
                        height={80}
                        animation="wave"
                        sx={{ borderRadius: 2, width: "100%" }}
                    />
                    ))
                : dialogList.map((dialog) => (
                    <Button
                        key={dialog.id}
                        fullWidth
                        onClick={() => handleDialogClick(dialog)}
                        sx={{
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            p: 2,
                            border: "1px solid transparent",
                            borderRadius: 2,
                            transition: "border 0.2s ease, color 0.2s ease",
                            "&:hover": {
                                backgroundColor: "transparent",
                                color: "#FCCD56",
                            },
                        }}
                    >
                        <Stack alignItems="center" spacing={0.5}>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{
                            fontSize: "1rem",
                            px: 2,
                            textAlign: "center",
                            whiteSpace: "normal",
                            wordBreak: "keep-all",
                            overflowWrap: "break-word",
                            }}
                        >
                            {dialog.title}
                        </Typography>

                        {dialog.subtitle && (
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontSize: "0.8rem",
                                px: 1,
                                textAlign: "center",
                                whiteSpace: "normal",
                            }}
                            >
                            {dialog.subtitle}
                            </Typography>
                        )}
                        </Stack>
                    </Button>
                    ))}
            </Box>
            </Stack>
        </Stack>
    );
};

export default LessonView;